import {
  useState, useEffect, forwardRef, useReducer, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import { useTranslation } from '@/helpers/translate';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Calendar from './calendar/index.js';
import dayjs from '@/helpers/dayjs';
import ClassCalendarStore from './calendar/store.js';
import Icon from '@/core/icon.js';

const FilterDate = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const [keySlug] = useState(item.id);
  // Format URL d'une date : `YYYYMMDD-<periode>` et, en mode horaire, `YYYYMMDD-0-<heures>`.
  const extractDataFromUrl = (values) => {
    if (!values) return [];
    const newValues = values.split('*');
    return newValues[0].split('|').reduce((acc, value) => {
      const valuetmp = value.split('-');
      const hours = parseInt(valuetmp[2], 10);
      // WEA-163 - 4e segment = heure d'arrivee souhaitee, pour qu'une URL partagee garde le
      // creneau et pas seulement sa duree.
      const startHour = parseInt(valuetmp[3], 10);
      const hasSlot = Number.isFinite(hours) && hours > 0 && Number.isFinite(startHour);
      acc.push({
        date: dayjs(valuetmp[0]).formatAsKey(),
        period: parseInt(valuetmp[1], 10),
        ...(Number.isFinite(hours) && hours > 0 ? { hours } : {}),
        ...(hasSlot ? {
          start: `${String(startHour).padStart(2, '0')}:00`,
          end: `${String(startHour + hours).padStart(2, '0')}:00`,
        } : {}),
      });
      return acc;
    }, []) || [];
  };

  const convertToCalendarStore = (values) => (values || []).reduce((acc, {
    date, period, hours, start, end,
  }) => {
    const key = dayjs(date.replace(/,000$/, '')).formatAsKey();
    acc.datepicked.push(key);
    acc.period[key] = period;
    if (start && end) acc.hours[key] = { start, end, hours };
    else if (hours > 0) acc.hours[key] = hours;
    return acc;
  }, { datepicked: [], period: {}, hours: {} });

  const [currentValue, setCurrentValue] = useReducer((prev, { value, runSearch }) => {
    let newValue = { ...prev };
    if (value) {
      newValue = { ...newValue, value };
    }
    if (runSearch) {
      newValue = { ...newValue, validValue: [...newValue.value], runSearch: nanoid() };
    }

    if (onFilter) {
      newValue = { ...newValue, validValue: [...newValue.value], runSearch: null };
    }

    return { ...newValue };
  }, {
    isChange: null,
    value: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    validValue: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
  });

  // eslint-disable-next-line max-len
  const [flexValue, setFlexValue] = useState((useSearchZustandStore.getState()?.initParams?.[item.slug] || '').split('*')?.[1] || 0);

  // eslint-disable-next-line max-len
  const [calendarStore, setCalendarStore] = useState(new ClassCalendarStore(convertToCalendarStore(currentValue.validValue)));
  // WEA-163 - le store calendrier est un objet mutable : ce jeton force le re-rendu de la
  // liste « heures par date » quand une date est cliquee en mode horaire.
  const [, setPickTick] = useState(null);

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (tmpValue === 'closeandrunsearch') {
      // console.log('search filter date - call run search', currentValue);
      if (onValid) return 'willCloseAndValid';
      // eslint-disable-next-line max-len
      // useSearchZustandStore.setState({filterEvent: {action:'itemfilterdatechange', slug: slugEvent}})
      return false;
    }
    if (!onScreen) return prev;

    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid) tmpValue = 'willCloseAndValid';
    }
    return tmpValue;
  }, false);

  const getValue = (from) => {
    if (from === 'url') {
      // eslint-disable-next-line max-len, no-unsafe-optional-chaining
      return currentValue?.validValue?.length ? (currentValue?.validValue || []).reduce((acc, {
        date, period, hours, start,
      }) => {
        const newAcc = [...acc];
        const day = dayjs(date.replace(/,000$/, '')).format('YYYYMMDD');
        if (!(hours > 0)) newAcc.push(`${day}-${period}`);
        else if (start) newAcc.push(`${day}-${period}-${hours}-${parseInt(start, 10)}`);
        else newAcc.push(`${day}-${period}-${hours}`);
        return newAcc;
      }, []).join('|') + (flexValue ? `*${flexValue}` : '') : null;
    }
    return (currentValue?.validValue?.length && currentValue.validValue) || null;
  };

  /*
    WEA-163 — la fourchette de prix compare un prix UNITAIRE : elle ne peut donc basculer sur
    le tarif horaire que si toutes les dates sont à l'heure. Dès qu'une date journée ou
    demi-journée entre dans la sélection — ou qu'aucune date n'est choisie — on reste aux
    bornes journée. Publié dans le store parce que ce sont les filtres de prix qui le lisent,
    et que le serveur en tire la même conclusion de son côté depuis `dates.values`.
  */
  useEffect(() => {
    const values = currentValue?.validValue || [];
    useSearchZustandStore.setState({
      /*
        WEA-163 — dès qu'une date est choisie, la fourchette de prix devient un BUDGET
        TOTAL pour la sélection (le serveur en tire la même conclusion depuis
        `dates.values`). Sans date, elle reste un prix unitaire à la journée.
      */
      budgetMode: !!values.length,
      hourlyPricing: !!values.length && values.every((v) => v?.hours > 0),
      // Creneaux souhaites, lus au report vers la fiche (ils ne transitent pas par le serveur).
      hourlySlots: values.reduce((acc, v) => (v?.hours > 0
        ? { ...acc, [v.date]: { hours: v.hours, start: v.start, end: v.end } }
        : acc), {}),
    });
  }, [currentValue]);

  /*
    Le creneau (`start`/`end`) ne part PAS au serveur : `SearchSpaceParamsDValues` ne declare
    que `date`, `period` et `hours`, et une cle inconnue ferait echouer la requete. Le filtre
    n'a de toute facon besoin que de la duree — le creneau sert au report vers la fiche.
  */
  const graphqlValues = () => (getValue() || []).map(({ start, end, ...rest }) => rest);

  const getOutput = () => ({
    keySlug,
    slug: item.slug,
    url: getValue('url'),
    values: getValue(),
    graphql: {
      [item.slug]: {
        values: graphqlValues(),
        ...(flexValue ? { flexibility: [parseInt(flexValue, 10)] } : {}),
      },
    },
  });

  const clearValue = (runSearch, e) => {
    if (e) e.stopPropagation();
    calendarStore.reset();
    setCurrentValue({ value: [], runSearch: true });
    setIsOpenModal(false);
  };

  function isSameDefault() {
    return !currentValue.validValue.length;
  }

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.runSearch) {
      setIsOpenModal('closeandrunsearch');
    }
  }, [currentValue.runSearch]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    }
    useSearchZustandStore.setState({
      datesSelected: calendarStore.period,
      datePicked: calendarStore.datepicked,
    });
  }, [isOpenModal]);

  // Une date n'est horaire que si le client l'a demande : pas de duree posee d'office ici.
  function convertCalendarStoreToValidValue() {
    return Object.entries(calendarStore.getPeriod()).reduce((acc, [date, period]) => {
      const newAcc1 = [...acc];
      const hours = calendarStore.hoursOf(date);
      const slot = calendarStore.slotOf(date);
      newAcc1.push({
        date,
        period,
        ...(hours > 0 ? { hours } : {}),
        ...(slot || {}),
      });
      return newAcc1;
    }, []);
  }

  function closeOnlyNotSaveValues(action) {
    setCalendarStore(new ClassCalendarStore(convertToCalendarStore(currentValue.validValue)));
    setIsOpenModal(action);
  }

  const hasValue = () => currentValue?.validValue?.length;

  const runSearch = () => {
    setCurrentValue({ value: convertCalendarStoreToValidValue(), runSearch: true });
  };

  const selectFlex = (val) => {
    setFlexValue(flexValue === val ? null : val);
  };

  const renderTitle = () => {
    if (onFilter) return <p className="pb-2 text-lg font-medium text-white">{t(item.title)}</p>;
    return (
      <p className="text-sm text-white flex justify-center whitespace-nowrap w-full items-center transition-all duration-200 py-[8px] px-[8px] rounded-lg cursor-pointer bg-purple-6 hover:bg-purple/70 group" onClick={() => setIsOpenModal(true)}>
        <Icon className="mr-2 transition-colors duration-200 size-4.5 text-purple-4/70 group-hover:text-white" name={item.slug} />
        <span className="transition-colors duration-200">{t(item.title)}</span>
        <Icon name="next" className="ml-2 w-2 h-2 transition-colors duration-200 transform rotate-90 text-purple-1 group-hover:text-white" />
      </p>
    );
  };

  const renderResume = () => (
    <div className="text-sm flex justify-center items-center transition text-white py-[8px] px-[8px] bg-purple rounded-lg cursor-pointer hover:bg-purple/80">
      <Icon className="hidden mr-2 w-5 h-5 text-white xl:inline" name={item.slug} />
      <p className="grid grid-flow-col auto-cols-max max-w-[160px] overflow-hidden gap-x-1" onClick={() => setIsOpenModal(true)}>
        {(currentValue?.validValue || []).map((v) => <span className="px-1 last:border-l border-white/30 last:pl-2" key={`filterdate${v.date}`}>{dayjs(v.date.replace(/,000$/, '')).format('DD/M')}</span>)}
      </p>
      {!flexValue || (
        <p className="flex items-center ml-1">
          <Icon name="plusminus" className="mx-1 text-white fill-current" />
          {flexValue}
          j
        </p>
      )}
      <button className="w-3" onClick={(e) => { clearValue(true, e); }}><Icon name="close" className="mx-1 w-full text-white fill-current size-1.5" /></button>
    </div>
  );

  const renderFlexibility = () => {
    if (!item?.options?.flexibility?.data) return null;
    return (
      <div className="">
        <div className="mb-1 text-sm text-purple-5/50">{t('core:flexibility')}</div>
        <div className="grid grid-cols-3 gap-x-2 mb-4">
          {item?.options?.flexibility?.data.map((f) => (
            <span
              key={`cmpsearchfilterdateflex${f.slug}`}
              className={`text-center text-sm justify-center py-1 px-3 flex items-center rounded cursor-pointer ${flexValue !== f.slug ? 'bg-purple-1/50 hover:bg-purple-1 text-purple-5' : 'text-purple bg-purple/10 hover:bg-purple/20'}`}
              onClick={() => selectFlex(f.slug)}
            >
              <Icon name="plusminus" className={`fill-current mx-1 ${flexValue !== f.slug || 'text-purple'}`} />
              {f.title}
              {' '}
              jour(s)
            </span>
          ))}
        </div>
      </div>
    );
  };

  /*
    WEA-163 - une ligne par date selectionnee. Chaque date porte SOIT une periode (journee,
    matin, apres-midi) SOIT un creneau horaire, jamais les deux : c'est la regle d'exclusivite
    du ticket, rendue visible ici. Un bouton fait passer la date a l'heure, une croix la fait
    revenir en periode, une autre la retire de la selection.

    La recherche ne connait aucun lieu, donc aucune disponibilite : le creneau saisi est un
    SOUHAIT. Le filtre serveur n'en garde que la DUREE ; le creneau lui-meme voyage jusqu'a la
    fiche, ou il est confronte aux tranches reellement libres.

    `pickTick` sert a re-rendre apres un clic dans le calendrier : le store est un objet
    mutable partage, pas un etat React.
  */
  const HOUR_MIN = 6;
  const HOUR_MAX = 23;
  const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
  const hourRange = (from, to) => Array.from(
    { length: Math.max(0, to - from + 1) },
    (_, i) => hourLabel(from + i),
  );
  const DEFAULT_SLOT = { start: '09:00', end: '10:00' };

  const periodLabel = (period) => {
    if (period === 1) return t('btn:morning');
    if (period === 2) return t('btn:afternoon');
    return '';
  };

  const removeDate = (dateKey) => {
    calendarStore.toggleDate(dateKey);
    setPickTick(nanoid());
    setCurrentValue({ value: convertCalendarStoreToValidValue() });
  };

  const setSlot = (dateKey, slot) => {
    calendarStore.setSlotForDate(dateKey, slot);
    setPickTick(nanoid());
    setCurrentValue({ value: convertCalendarStoreToValidValue() });
  };

  const backToPeriod = (dateKey) => {
    calendarStore.removeSlotForDate(dateKey);
    setPickTick(nanoid());
    setCurrentValue({ value: convertCalendarStoreToValidValue() });
  };

  const renderHoursByDate = () => {
    const picked = calendarStore.getDatesPicked();
    if (!picked.length) return null;
    const periodByDate = calendarStore.getPeriod();

    return (
      <div className="mb-4">
        <div className="mb-2 text-sm text-purple-5/50">{t('search:byhour.mixed_title')}</div>
        <div className="flex flex-col gap-y-2">
          {picked.map((dateKey) => {
            const slot = calendarStore.slotOf(dateKey);
            const isHourly = !!slot;
            const endOptions = slot
              ? hourRange(Number.parseInt(slot.start, 10) + 1, HOUR_MAX)
              : [];

            return (
              <div className="flex gap-2 items-center" key={`filterdatehours${dateKey}`}>
                <span
                  className="shrink-0 cursor-pointer"
                  title={t('btn:delete')}
                  onClick={() => removeDate(dateKey)}
                >
                  <Icon className="text-purple-5/50 fill-current size-3" name="close" />
                </span>
                <span className="flex-1 text-sm text-purple-6">
                  {dayjs(dateKey.replace(/,000$/, '')).format('ddd DD/MM')}
                  {!isHourly && periodLabel(periodByDate?.[dateKey]) && (
                    <span className="ml-2 text-purple-5/50">{periodLabel(periodByDate[dateKey])}</span>
                  )}
                </span>

                {isHourly ? (
                  <div className="flex gap-1 items-center">
                    <select
                      className="p-2 text-sm rounded-md bg-purple-0/80"
                      value={slot.start}
                      onChange={(ev) => {
                        const nextStart = ev.target.value;
                        const nextEnd = nextStart >= slot.end
                          ? hourLabel(Number.parseInt(nextStart, 10) + 1)
                          : slot.end;
                        setSlot(dateKey, { start: nextStart, end: nextEnd });
                      }}
                      aria-label={t('search:byhour.arrival')}
                    >
                      {hourRange(HOUR_MIN, HOUR_MAX - 1).map((h) => <option key={`s${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    <select
                      className="p-2 text-sm rounded-md bg-purple-0/80"
                      value={slot.end}
                      onChange={(ev) => setSlot(dateKey, { start: slot.start, end: ev.target.value })}
                      aria-label={t('search:byhour.departure')}
                    >
                      {endOptions.map((h) => <option key={`e${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    {/*
                      Le retour en periode est un bouton NOMME, pas une croix : deux croix
                      identiques sur la meme ligne (retirer la date / revenir en periode) ne
                      se distinguaient que par leur position. Il repond en miroir au bouton
                      « Passer a l'heure », et nomme sa destination plutot que son action.
                    */}
                    <span
                      className="flex gap-1 items-center px-2 py-1 ml-1 text-xs rounded-md border cursor-pointer shrink-0 text-purple-5/70 border-purple-5/20 hover:bg-purple-0"
                      title={t('search:byhour.back_to_period')}
                      onClick={() => backToPeriod(dateKey)}
                    >
                      <Icon className="fill-current text-purple-5/60 size-3" name="dates" />
                      {t('search:byhour.keep_period')}
                    </span>
                  </div>
                ) : (
                  <span
                    className="px-2 py-1 text-xs rounded-md border cursor-pointer text-purple border-purple/30 hover:bg-purple-0"
                    onClick={() => setSlot(dateKey, DEFAULT_SLOT)}
                  >
                    {t('search:byhour.to_hourly')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <>
      <Calendar store={calendarStore} onPick={() => setPickTick(nanoid())} />
      {renderHoursByDate()}
      {renderFlexibility()}
    </>
  );

  const renderModal = () => (
    <>
      <div
        className="absolute top-0 left-0 z-30 w-screen h-screen bg-purple-6/50"
        onClick={() => {
          closeOnlyNotSaveValues('outside');
        }}
      />
      <div className="-mt-[38px]">
        <div className="max-w-[400px] rounded-lg bg-white absolute z-40 px-6 pt-6 pb-4 shadow-button">
        <h1 className="mb-2 font-medium text-purple-6">{t(item.title)}</h1>
          <div className="text-sm">{renderContent()}</div>
          <button
            className="absolute top-4 right-4 size-5"
            onClick={() => {
              closeOnlyNotSaveValues('iconclose');
            }}
          >
            <Icon name="close" className="mx-1 fill-current size-4 text-purple-6/30 hover:text-black" />
          </button>
          <div className="flex justify-between items-center mt-4 mb-2">
            <button className="text-base rounded-none border-b text-purple-5/50 border-purple-5/50 hover:text-purple hover:border-purple" onClick={(e) => clearValue(true, e)}>{t('search:clean')}</button>
            <div>
              {renderButtonStartTour?.()}
              <button className="px-6 py-2 purple-gradient" onClick={() => runSearch()}>{t('search:save')}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderFilter = () => (
    <>
      {renderTitle()}
      {renderContent()}
    </>
  );

  return (
    <>
      {!(onScreen && !hasValue() && !isOpenModal) || renderTitle()}
      {!(onScreen && hasValue() && !isOpenModal) || renderResume()}
      {!(onScreen && hasValue() && isOpenModal) || (
      <>
        {renderResume()}
        {renderModal()}
      </>
      )}
      {!(onScreen && !hasValue() && isOpenModal) || (
      <>
        {renderTitle()}
        {renderModal()}
      </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default FilterDate;
